import { FC } from "react";

import { DonationFormCategory, FormTitleFontStyle, parseFormTitleFontStyle } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { LiveFormEditorV2StepTitleStateless, StepTitleFormData } from "./LiveFormEditorV2StepTitle.stateless";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useAIFeaturesEnabled } from "@/features/LiveFormEditor/Steps/helpers/useAIFeaturesEnabled";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";
import { LiveDonationData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

export const DonationLiveEditorStepTitle: FC = () => {
  const {
    apiFormObject: apiDonationForm,
    formCategory,
    stepProgress,
    editOrCreateMode,
    optionalFundraiserToken,
    featureFlags: { isDelightfulPagesActive },
  } = useLiveFormEditorContext<"donation-form">();
  const { t } = useTranslate();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const localizedDonationFormField = apiDonationForm.donationFormFields?.find(
    (field) => field.locale === apiDonationForm.locale
  );
  const initialTitle = localizedDonationFormField?.title || "";

  const defaultValues: StepTitleFormData = {
    title: initialTitle,
    titleFontStyle: parseFormTitleFontStyle(apiDonationForm.titleFontStyle) ?? FormTitleFontStyle.Classic,
  };
  const form = useForm<StepTitleFormData>({ defaultValues });

  const { mutateAsync: updateTitle } = trpc.updateDonationFormTitle.useMutation();
  const saveData = async (formData: StepTitleFormData) => {
    return updateTitle({
      donationFormId: apiDonationForm.id,
      title: formData.title,
      titleFontStyle: formData.titleFontStyle === FormTitleFontStyle.Classic ? null : formData.titleFontStyle,
      fundraiserToken: optionalFundraiserToken,
    });
  };

  const { isAIFeaturesEnabled } = useAIFeaturesEnabled();
  const generateDescriptionWithAIMutation = trpc.startDescriptionGenerationWithAI.useMutation();

  const { handleSaveAndNext } = useFormSaveAndNavigate<StepTitleFormData>({
    type: "donation-form",
    form,
    saveData,
    getSubmitTrackingProperties: (formData) => ({
      title_font_style: formData.titleFontStyle === FormTitleFontStyle.Classic ? null : formData.titleFontStyle,
    }),
    onFirstNextCompleted: () => {
      if (!isAIFeaturesEnabled) {
        return;
      }
      if (localizedDonationFormField?.description) {
        return;
      }
      generateDescriptionWithAIMutation.mutateAsync({
        formId: apiDonationForm.id,
        title: form.getValues("title"),
        formCategory,
        locale: apiDonationForm.locale,
      });
    },
  });

  const updateDataInPreview = (formData: StepTitleFormData, liveDataToMutate: LiveDonationData) => {
    const titleInPreview = formData.title || t("dashboard", "liveFormEditor.stepTitle.donationForm.placeholder");
    liveDataToMutate.donation.donationFormFields![0].title = titleInPreview;
    liveDataToMutate.donation.titleFontStyle =
      formData.titleFontStyle === FormTitleFontStyle.Classic ? null : formData.titleFontStyle;
  };
  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      updateDataInPreview(formData, liveDataToMutate);
    },
  });

  const getStepTitle = (category: DonationFormCategory): string => {
    switch (category) {
      case DonationFormCategory.DonationForm:
        return t("dashboard", "liveFormEditor.stepTitle.donationForm.title");
      case DonationFormCategory.PeerToPeerCampaign:
        return t("dashboard", "liveFormEditor.stepTitle.p2p.title");
      default:
        return t("dashboard", "liveFormEditor.stepTitle.donationForm.title");
    }
  };

  const { mutateAsync: deleteDonationForm } = trpc.deleteDonationForm.useMutation();

  const handleDeleteDraftForm = async () => {
    await deleteDonationForm({
      donationFormId: apiDonationForm.id,
      onlyDraft: true,
    });
  };

  return (
    <LiveFormEditorV2StepTitleStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      wording={{
        stepTitle: getStepTitle(formCategory),
      }}
      formId={apiDonationForm.id}
      onDeleteDraftForm={handleDeleteDraftForm}
      furthestStepReached={stepProgress.reached}
      showBackButton={editOrCreateMode === "create"}
      showFontStyleSelector={isDelightfulPagesActive}
      trackOnFontStyleChange={(fontStyle) =>
        logUserActionEvent(AmplitudeEvents.FormFontStyleChanged, { title_font_style: fontStyle })
      }
    />
  );
};
